import { useState, useEffect, useRef } from "react";
import { Button } from "../button";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../../../context/AuthContext";
import {
  Menu,
  X,
  LogOut,
  LayoutDashboard,
  ChevronRight,
  Home,
  Briefcase,
  Users,
  FileText,
  BookOpen,
  Heart,
  User,
} from "lucide-react";
import { ModeToggle } from "../../mode-toggle";
import { motion, AnimatePresence } from "framer-motion";
import api from "../../../api/client";
import { Logo } from "../../Logo";

export default function Navbar() {
  const { user, logout, role: authRole } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [userName, setUserName] = useState("");
  const mobileMenuRef = useRef(null);
  const userRole = (authRole || user?.role)?.toUpperCase();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 8);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname, location.search]);

  useEffect(() => {
    if (!mobileOpen) return;

    document.body.style.overflow = "hidden";

    const handleClickOutside = (event) => {
      const toggle = event.target.closest("[data-mobile-menu-toggle]");
      if (toggle) return;
      if (mobileMenuRef.current && !mobileMenuRef.current.contains(event.target)) {
        setMobileOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("touchstart", handleClickOutside);

    return () => {
      document.body.style.overflow = "";
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("touchstart", handleClickOutside);
    };
  }, [mobileOpen]);

  useEffect(() => {
    const fetchUserDetails = async () => {
      if (!user) return;

      try {
        if (userRole === "APPLIER") {
          try {
            const res = await api.get("/applier/profile");
            setUserName(res.data.fullName || user.email?.split("@")[0] || "User");
          } catch {
            setUserName(user.email?.split("@")[0] || "User");
          }
        } else if (userRole === "RECRUITER") {
          try {
            const res = await api.get("/recruiter/profile");
            setUserName(res.data.companyName || user.email?.split("@")[0] || "Recruiter");
          } catch {
            setUserName(user.email?.split("@")[0] || "Recruiter");
          }
        } else {
          setUserName(
            user.name ||
              user.firstName ||
              user.email?.split("@")[0] ||
              (userRole === "ADMIN" ? "Admin" : "User")
          );
        }
      } catch {
        setUserName(user?.email?.split("@")[0] || "User");
      }
    };

    fetchUserDetails();
  }, [user, userRole]);

  const handleLogout = () => {
    logout();
    navigate("/login");
    setMobileOpen(false);
  };

  const isActiveLink = (to) => {
    const [path, query] = to.split("?");
    if (query) {
      const params = new URLSearchParams(query);
      const current = new URLSearchParams(location.search);
      if (location.pathname !== path) return false;
      for (const [key, value] of params.entries()) {
        if (current.get(key) !== value) return false;
      }
      return true;
    }
    if (path === "/") return location.pathname === "/";
    if (path === "/applier/dashboard") {
      return location.pathname === path && !location.search.includes("tab=saved");
    }
    return location.pathname === path || location.pathname.startsWith(`${path}/`);
  };

  const applierLinks = [
    { label: "Home", to: "/", icon: Home },
    { label: "Jobs", to: "/jobs", icon: Briefcase },
    { label: "Dashboard", to: "/applier/dashboard", icon: LayoutDashboard },
    { label: "Saved", to: "/applier/dashboard?tab=saved", icon: Heart },
    { label: "Applications", to: "/applier/applications", icon: FileText },
    { label: "Blog", to: "/blog", icon: BookOpen },
  ];

  const getNavLinks = () => {
    if (!user) return [];

    switch (userRole) {
      case "APPLIER":
        return applierLinks;
      case "RECRUITER":
        return [
          { label: "Home", to: "/", icon: Home },
          { label: "Jobs", to: "/jobs", icon: Briefcase },
          { label: "Post Job", to: "/recruiter/post-job", icon: Briefcase },
          { label: "Applications", to: "/recruiter/applications", icon: Users },
          { label: "Dashboard", to: "/recruiter/dashboard", icon: LayoutDashboard },
          { label: "Blog", to: "/blog", icon: BookOpen },
        ];
      case "ADMIN":
        return [
          { label: "Home", to: "/", icon: Home },
          { label: "Jobs", to: "/jobs", icon: Briefcase },
          { label: "Users", to: "/admin/users", icon: Users },
          { label: "Dashboard", to: "/admin/dashboard", icon: LayoutDashboard },
          { label: "Blog", to: "/blog", icon: BookOpen },
        ];
      default:
        return [
          { label: "Home", to: "/", icon: Home },
          { label: "Blog", to: "/blog", icon: BookOpen },
        ];
    }
  };

  const navLinks = getNavLinks();

  const displayName =
    userName ||
    user?.email?.split("@")[0] ||
    (userRole === "ADMIN" ? "Admin" : "User");

  const displayRole =
    userRole === "APPLIER"
      ? "Applier"
      : userRole === "RECRUITER"
        ? "Recruiter"
        : userRole === "ADMIN"
          ? "Administrator"
          : "";

  const getInitials = () => {
    if (displayName?.length > 0) return displayName.charAt(0).toUpperCase();
    if (user?.email?.length > 0) return user.email.charAt(0).toUpperCase();
    return "U";
  };

  const profilePath =
    userRole === "APPLIER"
      ? "/profile"
      : userRole === "RECRUITER"
        ? "/recruiter/profile"
        : "/admin/profile";

  const linkClass = (active) =>
    `flex items-center gap-2 px-3 py-2 xl:px-3.5 xl:py-2.5 rounded-xl text-sm xl:text-[0.925rem] font-medium transition-all duration-200 whitespace-nowrap ${
      active
        ? "text-primary bg-primary/10 shadow-sm shadow-primary/10"
        : "text-muted-foreground hover:text-foreground hover:bg-white/40 dark:hover:bg-white/5"
    }`;

  return (
    <header
      className={`sticky top-0 z-50 w-full transition-all duration-300 border-b border-white/25 dark:border-white/10 glass-nav-footer ${
        scrolled ? "glass-nav-footer-scrolled shadow-sm shadow-black/5" : ""
      }`}
    >
      <div className="relative z-[60] max-w-[1440px] mx-auto px-3 sm:px-4 lg:px-6 xl:px-8">
        <div className="flex h-14 sm:h-16 xl:h-[4.25rem] items-center justify-between gap-3 xl:gap-6">
          <Link to="/" className="flex items-center shrink-0 group">
            <Logo className="h-8 sm:h-9 xl:h-10 w-auto max-w-[148px] sm:max-w-[168px] xl:max-w-[180px] transition-opacity group-hover:opacity-90" />
          </Link>

          {/* Desktop nav */}
          <nav className="hidden lg:flex items-center gap-0.5 xl:gap-1 flex-1 justify-center min-w-0">
            {(user ? navLinks : [
              { label: "Home", to: "/", icon: Home },
              { label: "Blog", to: "/blog", icon: BookOpen },
            ]).map((link) => {
              const Icon = link.icon;
              const active = isActiveLink(link.to);
              return (
                <Link key={link.to} to={link.to} className={linkClass(active)}>
                  <Icon className="w-4 h-4 shrink-0" />
                  <span className="truncate">{link.label}</span>
                </Link>
              );
            })}
          </nav>

          {/* Desktop actions */}
          <div className="hidden lg:flex items-center gap-2 xl:gap-3 shrink-0">
            <ModeToggle />
            {user ? (
              <>
                <button
                  onClick={() => navigate(profilePath)}
                  className="flex items-center gap-2 pl-1.5 pr-3 py-1.5 rounded-xl border border-border/60 dark:border-border bg-background/50 dark:bg-secondary/60 hover:bg-primary/5 transition-all"
                >
                  <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-primary to-accent flex items-center justify-center text-white font-semibold text-sm shadow-sm">
                    {getInitials()}
                  </div>
                  <div className="hidden xl:block text-left max-w-[140px]">
                    <p className="text-sm font-medium truncate">{displayName}</p>
                    <p className="text-[10px] text-muted-foreground truncate">{displayRole}</p>
                  </div>
                </button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleLogout}
                  className="text-muted-foreground hover:text-destructive hover:bg-destructive/10 rounded-xl"
                >
                  <LogOut className="w-4 h-4 xl:mr-2" />
                  <span className="hidden xl:inline">Logout</span>
                </Button>
              </>
            ) : (
              <>
                <Button asChild variant="ghost" size="sm" className="rounded-xl">
                  <Link to="/login">Login</Link>
                </Button>
                <Button asChild size="sm" className="rounded-xl shadow-md shadow-primary/20">
                  <Link to="/register">
                    Get Started
                    <ChevronRight className="ml-1 w-4 h-4" />
                  </Link>
                </Button>
              </>
            )}
          </div>

          {/* Mobile actions */}
          <div className="flex lg:hidden items-center gap-2">
            <ModeToggle />
            <button
              data-mobile-menu-toggle
              onClick={() => setMobileOpen(!mobileOpen)}
              className="p-2 rounded-xl border border-white/30 dark:border-white/10 bg-white/40 dark:bg-white/[0.06] backdrop-blur-sm hover:bg-white/55 dark:hover:bg-white/10 transition-colors"
              aria-label={mobileOpen ? "Close menu" : "Open menu"}
              aria-expanded={mobileOpen}
            >
              <AnimatePresence mode="wait" initial={false}>
                {mobileOpen ? (
                  <motion.div key="close" initial={{ opacity: 0, rotate: -90 }} animate={{ opacity: 1, rotate: 0 }} exit={{ opacity: 0, rotate: 90 }} transition={{ duration: 0.15 }}>
                    <X className="w-5 h-5" />
                  </motion.div>
                ) : (
                  <motion.div key="menu" initial={{ opacity: 0, rotate: 90 }} animate={{ opacity: 1, rotate: 0 }} exit={{ opacity: 0, rotate: -90 }} transition={{ duration: 0.15 }}>
                    <Menu className="w-5 h-5" />
                  </motion.div>
                )}
              </AnimatePresence>
            </button>
          </div>
        </div>
      </div>

      {/* Mobile menu — fixed overlay, does not push page content */}
      <AnimatePresence>
        {mobileOpen && (
          <>
            <motion.button
              type="button"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="fixed inset-0 z-[45] lg:hidden bg-black/25 dark:bg-black/50 backdrop-blur-[2px]"
              aria-label="Close menu"
              onClick={() => setMobileOpen(false)}
            />
            <motion.div
              ref={mobileMenuRef}
              initial={{ opacity: 0, y: -12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -12 }}
              transition={{ duration: 0.22, ease: "easeOut" }}
              className="fixed left-0 right-0 top-14 sm:top-16 z-[55] lg:hidden border-t border-white/30 dark:border-white/10 glass-mobile-menu shadow-xl shadow-black/15 max-h-[calc(100dvh-3.5rem)] sm:max-h-[calc(100dvh-4rem)] overflow-y-auto overscroll-contain"
            >
              <div className="max-w-[1440px] mx-auto px-3 py-4 space-y-3">
                {user && (
                  <button
                    onClick={() => { setMobileOpen(false); navigate(profilePath); }}
                    className="w-full flex items-center gap-3 p-3 rounded-xl border border-border/60 dark:border-white/10 bg-background/70 dark:bg-white/[0.08] text-left"
                  >
                  <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-primary to-accent flex items-center justify-center text-white font-bold">
                    {getInitials()}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="font-semibold text-sm truncate">{displayName}</p>
                    <p className="text-xs text-muted-foreground truncate">{user.email}</p>
                  </div>
                  <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-primary/10 text-primary shrink-0">
                    {displayRole}
                  </span>
                </button>
              )}

              <div className="space-y-1">
                {(user ? navLinks : [
                  { label: "Home", to: "/", icon: Home },
                  { label: "Blog", to: "/blog", icon: BookOpen },
                  { label: "Login", to: "/login", icon: User },
                ]).map((link) => {
                  const Icon = link.icon;
                  const active = isActiveLink(link.to);
                  return (
                    <Link
                      key={link.to}
                      to={link.to}
                      onClick={() => setMobileOpen(false)}
                      className={`flex items-center gap-3 px-3 py-2.5 rounded-xl transition-colors ${
                        active ? "bg-primary/10 text-primary" : "hover:bg-muted/50"
                      }`}
                    >
                      <div className={`p-1.5 rounded-lg ${active ? "bg-primary text-primary-foreground" : "bg-muted/80"}`}>
                        <Icon className="w-4 h-4" />
                      </div>
                      <span className="font-medium text-sm flex-1">{link.label}</span>
                      <ChevronRight className="w-4 h-4 text-muted-foreground/60" />
                    </Link>
                  );
                })}
              </div>

              <div className="pt-3 border-t border-white/30 dark:border-white/10">
                {user ? (
                  <div className="grid grid-cols-2 gap-2">
                    <Button variant="outline" className="rounded-xl h-10" onClick={() => { setMobileOpen(false); navigate(profilePath); }}>
                      <User className="w-4 h-4 mr-1.5" />
                      Profile
                    </Button>
                    <Button variant="destructive" className="rounded-xl h-10" onClick={handleLogout}>
                      <LogOut className="w-4 h-4 mr-1.5" />
                      Logout
                    </Button>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 gap-2">
                    <Button variant="outline" className="rounded-xl h-10" asChild>
                      <Link to="/login" onClick={() => setMobileOpen(false)}>Login</Link>
                    </Button>
                    <Button className="rounded-xl h-10 shadow-md shadow-primary/20" asChild>
                      <Link to="/register" onClick={() => setMobileOpen(false)}>Sign Up</Link>
                    </Button>
                  </div>
                )}
              </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </header>
  );
}
